import { defineComponent as _defineComponent } from 'vue';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CircleAlert, CircleCheck, Download, Info, TriangleAlert, X as CloseIcon } from 'lucide-vue-next';
import EnButton from 'Components/base/EnButton';
import { SnackbarType } from 'Components/types/SnackbarType.js';
import { cn } from 'Components/primitives/utils';
import { snackbarIconVariants, snackbarVariants } from './snackbarVariants';
export default /*@__PURE__*/ _defineComponent({
    ...{
        inheritAttrs: false,
    },
    __name: 'EnSnackbar',
    props: {
        timeout: { type: Number, required: false, default: 5000 },
        dense: { type: Boolean, required: false, default: false },
        type: { type: null, required: false, default: SnackbarType.INFO },
        class: { type: [Boolean, null, String, Object, Array], required: false, skipCheck: true, default: undefined }
    },
    emits: ["close"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        /**
         * Emitted whenever the snackbar closes — through the close button or the timeout.
         */
        const emit = __emit;
        const { t } = useI18n();
        const typeIcons = {
            info: Info,
            warning: CircleAlert,
            error: TriangleAlert,
            success: CircleCheck,
            filedownload: Download,
        };
        const isVisible = ref(false);
        const messages = ref([]);
        let closeTimeout;
        /**
         * The `type` prop carries the screaming-case `SnackbarType` values, so it is
         * normalised once here. Anything unrecognised falls back to `info`.
         */
        const variant = computed(() => String(props.type).toLowerCase());
        const icon = computed(() => typeIcons[variant.value]);
        const iconClass = computed(() => snackbarIconVariants({ type: variant.value, dense: props.dense }));
        const surfaceClass = computed(() => cn(snackbarVariants({ dense: props.dense }), props.class));
        /**
         *  Pushes a new message to the snackbar. The message will
         *  be displayed to the user.
         */
        function pushMessage(message) {
            if (message && !messages.value.includes(message)) {
                messages.value = [message, ...messages.value];
            }
            isVisible.value = true;
            clearTimeout(closeTimeout);
            closeTimeout = setTimeout(close, props.timeout);
        }
        function clear() {
            clearTimeout(closeTimeout);
            messages.value = [];
            isVisible.value = false;
        }
        function close() {
            clear();
            emit('close');
        }
        onBeforeUnmount(() => clearTimeout(closeTimeout));
        __expose({
            pushMessage,
            clear,
        });
        const __returned__ = { props, emit, t, typeIcons, isVisible, messages, get closeTimeout() { return closeTimeout; }, set closeTimeout(v) { closeTimeout = v; }, variant, icon, iconClass, surfaceClass, pushMessage, clear, close, get CloseIcon() { return CloseIcon; }, get EnButton() { return EnButton; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
